import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import EntityTable, { StatusBadge, RowActionMenu } from "components/EntityTable";
import ConfirmDialog from "components/ConfirmDialog";
import ModalShell from "components/ModalShell";
import usePaginatedResource from "hooks/usePaginatedResource";
import useDebounce from "hooks/useDebounce";

import {
  listForms,
  deleteForm,
  activateForm,
  deactivateForm,
  exportFormSubmissions,
} from "api/endpoints/forms";

// Forms content view — a server-paginated table of forms with create/edit via
// the form builder.
function Forms({ websiteId: propWebsiteId, websiteName: propWebsiteName }) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const [confirmFormId, setConfirmFormId] = useState(null);
  const [toggleDialog, setToggleDialog] = useState({ open: false, formId: null, active: false });
  const [downloadDialog, setDownloadDialog] = useState({
    open: false,
    formId: null,
    startDate: "",
    endDate: "",
    status: "",
    limit: 1000,
  });

  // Support both prop and query param.
  const params = new URLSearchParams(location.search);
  const websiteId = propWebsiteId || params.get("website_id");
  const websiteName = propWebsiteName || params.get("website_name");

  const fetchForms = useCallback(
    (pageParams) => {
      if (!websiteId) return { items: [], total: 0 };
      return listForms(websiteId, { ...pageParams, search: debouncedSearch });
    },
    [websiteId, debouncedSearch]
  );

  const { rows, total, loading, page, pageSize, setPage, setPageSize, refresh } =
    usePaginatedResource(fetchForms);

  // A new search term restarts at the first page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, setPage]);

  // ------------------- NAVIGATION -------------------
  const handleCreateForm = () => {
    if (!websiteName) return;
    navigate(`/websites/${encodeURIComponent(websiteName)}/form-builder`, {
      state: { websiteId, websiteName, mode: "create", from: location.pathname + location.search },
    });
  };

  const handleEditForm = (form) => {
    if (!websiteName) return;
    navigate(`/websites/${encodeURIComponent(websiteName)}/form-builder/${form.slug}`, {
      state: {
        websiteId,
        websiteName,
        formId: form.form_id,
        mode: "edit",
        from: location.pathname + location.search,
      },
    });
  };

  const handleViewSubmissions = (formId) => {
    if (!websiteName) return;
    navigate(`/websites/${encodeURIComponent(websiteName)}/forms/${formId}/submissions`, {
      state: { websiteId, websiteName, from: location.pathname + location.search },
    });
  };

  // ------------------- MUTATIONS -------------------
  const confirmDeleteForm = async () => {
    try {
      await deleteForm(websiteId, confirmFormId);
      enqueueSnackbar("Form deleted", { variant: "success" });
      refresh();
    } catch {
      enqueueSnackbar("Failed to delete form", { variant: "error" });
    } finally {
      setConfirmFormId(null);
    }
  };

  const confirmToggleActive = async () => {
    const { formId, active } = toggleDialog;
    try {
      if (active) await deactivateForm(websiteId, formId);
      else await activateForm(websiteId, formId);
      enqueueSnackbar(`Form ${active ? "deactivated" : "activated"}`, { variant: "success" });
      refresh();
    } catch {
      enqueueSnackbar("Failed to toggle form status", { variant: "error" });
    } finally {
      setToggleDialog({ open: false, formId: null, active: false });
    }
  };

  // ------------------- DOWNLOAD -------------------
  const openDownload = (formId) =>
    setDownloadDialog({ open: true, formId, startDate: "", endDate: "", status: "", limit: 1000 });

  const closeDownload = () => setDownloadDialog((d) => ({ ...d, open: false }));

  const handleDownloadSubmit = async () => {
    try {
      const { formId, startDate, endDate, status, limit } = downloadDialog;
      const blob = await exportFormSubmissions(websiteId, formId, {
        startDate,
        endDate,
        status,
        limit,
      });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `submissions_${formId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      enqueueSnackbar("Download started", { variant: "success" });
      closeDownload();
    } catch {
      enqueueSnackbar("Failed to download CSV", { variant: "error" });
    }
  };

  // ------------------- TABLE -------------------
  const columns = [
    { field: "name", headerName: "Form Name", flex: 1, minWidth: 180 },
    { field: "slug", headerName: "Slug", flex: 1, minWidth: 160 },
    {
      field: "active",
      headerName: "Status",
      flex: 0.7,
      minWidth: 120,
      renderCell: ({ value }) => <StatusBadge active={value} />,
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <RowActionMenu
          actions={[
            { label: "Edit", icon: "edit", onClick: () => handleEditForm(row) },
            {
              label: "View Submissions",
              icon: "visibility",
              onClick: () => handleViewSubmissions(row.form_id),
            },
            {
              label: "Download Submissions",
              icon: "download",
              onClick: () => openDownload(row.form_id),
            },
            {
              label: row.active ? "Deactivate" : "Activate",
              icon: row.active ? "block" : "check_circle",
              onClick: () =>
                setToggleDialog({ open: true, formId: row.form_id, active: row.active }),
            },
            {
              label: "Delete",
              icon: "delete",
              color: "error",
              onClick: () => setConfirmFormId(row.form_id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <MDTypography variant="h5" fontWeight="bold">
          Forms
        </MDTypography>
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<Icon>add</Icon>}
          onClick={handleCreateForm}
        >
          Create New Form
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <MDBox mb={2} maxWidth={320}>
            <MDInput
              fullWidth
              placeholder="Search by name or slug"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{ endAdornment: <Icon fontSize="small">search</Icon> }}
            />
          </MDBox>
          <EntityTable
            rows={rows}
            columns={columns}
            getRowId={(row) => row.form_id}
            loading={loading}
            paginationMode="server"
            rowCount={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onRowClick={({ row }) => handleViewSubmissions(row.form_id)}
          />
        </MDBox>
      </Card>

      {/* Download submissions */}
      <ModalShell
        open={downloadDialog.open}
        onClose={closeDownload}
        title="Download Submissions"
        actions={
          <>
            <MDButton onClick={closeDownload} color="secondary" variant="text">
              Cancel
            </MDButton>
            <MDButton onClick={handleDownloadSubmit} color="info" variant="gradient">
              Download
            </MDButton>
          </>
        }
      >
        <MDBox sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TextField
            label="Start Date"
            type="date"
            value={downloadDialog.startDate}
            onChange={(e) => setDownloadDialog({ ...downloadDialog, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            value={downloadDialog.endDate}
            onChange={(e) => setDownloadDialog({ ...downloadDialog, endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Status"
            value={downloadDialog.status || ""}
            onChange={(e) => setDownloadDialog({ ...downloadDialog, status: e.target.value })}
            fullWidth
          >
            {["pending", "spam", "processed"].map((status) => (
              <MenuItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Limit"
            type="number"
            value={downloadDialog.limit}
            onChange={(e) => setDownloadDialog({ ...downloadDialog, limit: e.target.value })}
            fullWidth
          />
        </MDBox>
      </ModalShell>

      {/* Activate / deactivate */}
      <ConfirmDialog
        open={toggleDialog.open}
        title={toggleDialog.active ? "Deactivate Form" : "Activate Form"}
        message={`Are you sure you want to ${
          toggleDialog.active ? "deactivate" : "activate"
        } this form?`}
        confirmText={toggleDialog.active ? "Deactivate" : "Activate"}
        confirmColor="info"
        onConfirm={confirmToggleActive}
        onClose={() => setToggleDialog({ open: false, formId: null, active: false })}
      />

      {/* Delete */}
      <ConfirmDialog
        open={confirmFormId !== null}
        title="Delete Form"
        message="Are you sure you want to delete this form?"
        confirmText="Delete"
        onConfirm={confirmDeleteForm}
        onClose={() => setConfirmFormId(null)}
      />
    </MDBox>
  );
}

Forms.propTypes = {
  websiteId: PropTypes.string,
  websiteName: PropTypes.string,
};

export default Forms;
